import { Trans, useLingui } from "@lingui/react/macro";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";

import {
  apiGetQuestionnairesQuestionnaireTypeLanguage,
  QuestionnaireTypes,
  SupportedLanguages,
} from "../../../api";
import { ApiConnector } from "../../../api/api-connector";
import { Button } from "../../../components/ui-kit/button";
import { useDialog } from "../../../components/ui-kit/dialog-provider";
import { DateInput } from "../../../components/ui-kit/fields/date-input";
import { TextInput } from "../../../components/ui-kit/fields/text-input";
import { Form } from "../../../components/ui-kit/form/form";
import { RequiredAsterisk } from "../../../components/ui-kit/required-asterisk";
import { Spinner } from "../../../components/ui-kit/spinner";
import { requireNonNull } from "../../../utils/utils";
import { Patient } from "../patient";
import { Question } from "./question";
import { QuestionnaireModel } from "./questionnaire-model";

export const Questionnaire = observer(function Questionnaire(props: {
  patient: Patient;
  api: ApiConnector;
  type: QuestionnaireTypes;
  onClose: () => void;
}) {
  const { t } = useLingui();
  const [loading, setLoading] = useState(true);
  const [model, setModel] = useState<QuestionnaireModel>();
  const { confirm } = useDialog();
  const userUUID = props.api.requireUserUUID();

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await apiGetQuestionnairesQuestionnaireTypeLanguage({
          path: {
            language: SupportedLanguages.en,
            questionnaire_type: props.type,
          },
        });

        const m = new QuestionnaireModel(
          res.data,
          props.patient.patientId,
          userUUID,
        );
        setModel(m);
      } finally {
        setLoading(false);
      }
    };

    void fetchData();
  }, [props.type, props.patient.patientId, userUUID]);

  if (loading) {
    return <Spinner />;
  }
  if (!model) {
    // The questionnaire failed to load.
    return null;
  }

  return (
    <div>
      <Form
        onSubmit={async () => {
          await model.submit();
          props.onClose();
        }}
      >
        <div>
          <Trans>
            (<RequiredAsterisk /> = mandatory)
          </Trans>
        </div>

        <div className="h-5" />

        {model.questionnaire.questions.map((question) => (
          <div key={question.question_id}>
            <Question
              question={question}
              response={requireNonNull(
                model.responses.get(question.question_id),
              )}
            />
            <div className="h-5" />
          </div>
        ))}

        <div>
          <label>
            <div>
              <b>
                <Trans>Comment:</Trans>
              </b>
            </div>
            <div className="h-1" />
            <TextInput model={model} attr="comment" multiline="autosize" />
          </label>
        </div>

        <div className="h-5" />

        <div>
          <label>
            <div>
              <b>
                <Trans>Date filled:</Trans>
              </b>{" "}
              <RequiredAsterisk />
            </div>
            <div className="h-1" />
            <DateInput
              attr="fillingEnd"
              model={model}
              type="datetime-local"
              required
            />
          </label>
        </div>

        <div className="h-4" />

        <div className="flex gap-1">
          <Button type="submit">
            <Trans>Submit</Trans>
          </Button>

          <Button
            onClick={async () => {
              if (await confirm(t`Discard data?`)) {
                props.onClose();
              }
            }}
          >
            <Trans>Cancel</Trans>
          </Button>
        </div>
      </Form>
    </div>
  );
});
